import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../util/types';
import {
  constructQueryParamName,
  convertCategoriesToSelectTreeOptions,
  parseSelectFilterOptions,
} from '../../util/search';
import { departmentFromCategoryName } from '../../util/racingThemeDepartment';

const RACING_SUPPORTED_SCHEMA_TYPES = new Set([
  'category',
  'listingType',
  'price',
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
]);

/**
 * Split availableFilters into Racing-mappable groups and leftover FilterComponent configs.
 *
 * @param {Array} availableFilters
 * @returns {{ racingFilters: Array, leftoverFilters: Array }}
 */
export const splitFiltersForRacingUi = (availableFilters = []) => {
  const racingFilters = [];
  const leftoverFilters = [];

  availableFilters.forEach(config => {
    if (!config) {
      return;
    }
    if (config.schemaType === 'keywords') {
      // Keywords live in the banner.
      return;
    }
    if (RACING_SUPPORTED_SCHEMA_TYPES.has(config.schemaType)) {
      racingFilters.push(config);
    } else {
      leftoverFilters.push(config);
    }
  });

  return { racingFilters, leftoverFilters };
};

const getSelectedOptions = (validQueryParams, queryParamName) => {
  const raw = validQueryParams?.[queryParamName];
  if (!raw) {
    return [];
  }
  return parseSelectFilterOptions(String(raw)).filter(Boolean);
};

/**
 * Map SelectTree category options into Racing checkbox tree nodes.
 * Checked state follows nested URL params: OR within a level, AND across levels.
 *
 * @param {Array} treeOptions
 * @param {Array<Array<string>>} selectedByLevel ids selected per level
 * @param {number} depth
 * @returns {Array}
 */
const mapCategoryTreeOptions = (treeOptions = [], selectedByLevel = [], depth = 0) =>
  treeOptions.map(opt => {
    const id = String(opt.option);
    const children = Array.isArray(opt.suboptions) && opt.suboptions.length > 0
      ? mapCategoryTreeOptions(opt.suboptions, selectedByLevel, depth + 1)
      : undefined;
    const selectedAtLevel = selectedByLevel[depth] || [];
    return {
      id,
      label: opt.label,
      checked: selectedAtLevel.includes(id),
      level: depth + 1,
      ...(children ? { children } : {}),
    };
  });

/**
 * Find path of option nodes from root to targetId.
 *
 * @param {Array} options
 * @param {string} targetId
 * @param {Array} path
 * @returns {Array|null}
 */
export const findCategoryOptionPath = (options = [], targetId, path = []) => {
  for (const option of options) {
    const nextPath = [...path, option];
    if (option.id === targetId) {
      return nextPath;
    }
    if (option.children?.length) {
      const found = findCategoryOptionPath(option.children, targetId, nextPath);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

/**
 * Collect all checked option ids in a category tree.
 *
 * @param {Array} options
 * @returns {Array<string>}
 */
const collectCheckedCategoryIds = (options = []) => {
  const ids = [];
  const walk = nodes => {
    nodes.forEach(node => {
      if (node.checked) {
        ids.push(node.id);
      }
      if (node.children?.length) {
        walk(node.children);
      }
    });
  };
  walk(options);
  return ids;
};

/**
 * Collect descendant option ids (not including the node itself).
 *
 * @param {Object|null} option
 * @returns {Array<string>}
 */
const collectDescendantCategoryIds = option => {
  if (!option?.children?.length) {
    return [];
  }
  const ids = [];
  const walk = nodes => {
    nodes.forEach(node => {
      ids.push(node.id);
      if (node.children?.length) {
        walk(node.children);
      }
    });
  };
  walk(option.children);
  return ids;
};

/**
 * Find a category option node by id.
 *
 * @param {Array} options
 * @param {string} targetId
 * @returns {Object|null}
 */
const findCategoryOptionNode = (options = [], targetId) => {
  for (const option of options) {
    if (option.id === targetId) {
      return option;
    }
    if (option.children?.length) {
      const found = findCategoryOptionNode(option.children, targetId);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

/**
 * Collect all checked category nodes for active filter chips.
 * Includes both parent and subcategory selections.
 *
 * @param {Array} options
 * @returns {Array}
 */
const collectCheckedCategoryNodes = (options = []) => {
  const checked = [];
  const walk = nodes => {
    nodes.forEach(node => {
      if (node.checked) {
        checked.push(node);
      }
      if (node.children?.length) {
        walk(node.children);
      }
    });
  };
  walk(options);
  return checked;
};

/**
 * Build Sharetribe category query params from selected option ids.
 * Same-level values are comma-joined (OR); levels are separate params (AND).
 *
 * @param {Array<string>} queryParamNames
 * @param {Array} options
 * @param {Array<string>} selectedIds
 * @returns {Object}
 */
const buildCategoryParamsFromSelectedIds = (queryParamNames, options, selectedIds) => {
  const levelSets = queryParamNames.map(() => new Set());

  selectedIds.forEach(id => {
    const path = findCategoryOptionPath(options, id);
    if (!path) {
      return;
    }
    path.forEach((node, i) => {
      if (i < levelSets.length) {
        levelSets[i].add(node.id);
      }
    });
  });

  return queryParamNames.reduce((acc, name, i) => {
    const values = Array.from(levelSets[i]);
    acc[name] = values.length > 0 ? values.join(',') : null;
    return acc;
  }, {});
};

/**
 * Format nested category selection into Sharetribe query params.
 * Multi-select: OR within each categoryLevel*, AND across levels.
 *
 * @param {Object} group
 * @param {string} optionId
 * @param {boolean} checked
 * @returns {Object}
 */
export const formatCategoryChangeParams = (group, optionId, checked) => {
  const queryParamNames = group.queryParamNames || [];
  const options = group.options || [];
  if (queryParamNames.length === 0) {
    return {};
  }

  const emptyParams = queryParamNames.reduce((acc, name) => {
    acc[name] = null;
    return acc;
  }, {});

  const currentlySelected = collectCheckedCategoryIds(options);
  let nextSelected;

  if (checked) {
    nextSelected = currentlySelected.includes(optionId)
      ? currentlySelected
      : [...currentlySelected, optionId];
  } else {
    const node = findCategoryOptionNode(options, optionId);
    const descendants = new Set(collectDescendantCategoryIds(node));
    nextSelected = currentlySelected.filter(id => id !== optionId && !descendants.has(id));
  }

  if (nextSelected.length === 0) {
    return emptyParams;
  }

  return buildCategoryParamsFromSelectedIds(queryParamNames, options, nextSelected);
};

/**
 * Build Racing Theme filter groups from Sharetribe filter configs + URL state.
 *
 * @param {Object} params
 * @param {Array} params.racingFilters
 * @param {Array} params.listingCategories
 * @param {Object} params.validQueryParams
 * @param {Object} params.intl
 * @returns {Array}
 */
export const buildRacingFilterGroups = ({
  racingFilters = [],
  listingCategories = [],
  validQueryParams = {},
  intl,
}) => {
  return racingFilters
    .map(config => {
      const { key, schemaType } = config;

      if (schemaType === 'price') {
        const raw = validQueryParams?.price;
        const [min = '', max = ''] = raw ? String(raw).split(',') : [];
        return {
          id: key,
          title: intl
            ? intl.formatMessage({ id: 'FilterComponent.priceLabel' })
            : 'Price Range',
          type: 'price',
          schemaType,
          queryParamName: 'price',
          minValue: min,
          maxValue: max,
          config,
        };
      }

      if (schemaType === 'category') {
        const { scope, nestedParams } = config;
        const queryParamNames = nestedParams?.map(p => constructQueryParamName(p, scope)) || [];
        const selectedByLevel = queryParamNames.map(name =>
          getSelectedOptions(validQueryParams, name).map(String)
        );
        const options = mapCategoryTreeOptions(
          convertCategoriesToSelectTreeOptions(listingCategories),
          selectedByLevel
        );

        return {
          id: key,
          title: intl
            ? intl.formatMessage({ id: 'FilterComponent.categoryLabel' })
            : 'Category',
          type: 'checkbox',
          schemaType,
          queryParamNames,
          queryParamName: queryParamNames[0],
          selectionMode: 'multi',
          options,
          config,
        };
      }

      if (schemaType === 'listingType') {
        const { scope, options = [] } = config;
        const queryParamName = constructQueryParamName(key, scope);
        const selected = getSelectedOptions(validQueryParams, queryParamName);
        return {
          id: key,
          title: intl
            ? intl.formatMessage({ id: 'FilterComponent.listingTypeLabel' })
            : 'Listing type',
          type: 'checkbox',
          schemaType,
          queryParamName,
          selectionMode: 'single',
          options: options.map(opt => ({
            id: String(opt.option || opt.id),
            label: opt.label,
            checked: selected.includes(String(opt.option || opt.id)),
          })),
          config,
        };
      }

      if (schemaType === SCHEMA_TYPE_ENUM || schemaType === SCHEMA_TYPE_MULTI_ENUM) {
        const { scope, enumOptions = [], filterConfig = {} } = config;
        const queryParamName = constructQueryParamName(key, scope);
        const selected = getSelectedOptions(validQueryParams, queryParamName);
        const isSingle =
          schemaType === SCHEMA_TYPE_ENUM && filterConfig.filterType === 'SelectSingleFilter';

        return {
          id: key,
          title: filterConfig.label || key,
          type: 'checkbox',
          schemaType,
          queryParamName,
          selectionMode: isSingle ? 'single' : 'multi',
          searchMode: filterConfig.searchMode,
          options: enumOptions.map(opt => ({
            id: String(opt.option),
            label: opt.label,
            checked: selected.includes(String(opt.option)),
          })),
          config,
        };
      }

      return null;
    })
    .filter(Boolean);
};

/**
 * Format checkbox selection into Sharetribe URL param payload.
 *
 * @param {Object} group
 * @param {string} optionId
 * @param {boolean} checked
 * @returns {Object}
 */
export const formatOptionChangeParams = (group, optionId, checked) => {
  const { queryParamName, selectionMode, searchMode, schemaType, options = [] } = group;

  if (schemaType === 'category') {
    return formatCategoryChangeParams(group, optionId, checked);
  }

  if (!queryParamName) {
    return {};
  }

  if (selectionMode === 'single') {
    return { [queryParamName]: checked ? optionId : null };
  }

  const currentlySelected = options.filter(o => o.checked).map(o => o.id);
  let nextSelected;
  if (checked) {
    nextSelected = currentlySelected.includes(optionId)
      ? currentlySelected
      : [...currentlySelected, optionId];
  } else {
    nextSelected = currentlySelected.filter(id => id !== optionId);
  }

  if (nextSelected.length === 0) {
    return { [queryParamName]: null };
  }

  const modePrefix =
    schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  return { [queryParamName]: `${modePrefix}${nextSelected.join(',')}` };
};

/**
 * Format price range into Sharetribe price query param.
 *
 * @param {Object} group
 * @param {{ min: string|number, max: string|number }} values
 * @returns {Object}
 */
export const formatPriceChangeParams = (group, values) => {
  const minRaw = values?.min;
  const maxRaw = values?.max;
  const hasMin = minRaw !== '' && minRaw != null;
  const hasMax = maxRaw !== '' && maxRaw != null;

  if (!hasMin && !hasMax) {
    return { price: null };
  }

  const configMin = group?.config?.min ?? 0;
  const configMax = group?.config?.max ?? 100000;
  const min = hasMin ? Number(minRaw) : configMin;
  const max = hasMax ? Number(maxRaw) : configMax;

  if (Number.isNaN(min) || Number.isNaN(max)) {
    return {};
  }

  return { price: `${min},${max}` };
};

/**
 * Build active filter chips from URL params + racing filter groups.
 *
 * @param {Object} params
 * @param {Array} params.filterGroups
 * @param {Object} params.validQueryParams
 * @returns {Array<{ id: string, label: string, queryParamName: string, clearParams: Object }>}
 */
export const buildActiveFilterChips = ({ filterGroups = [], validQueryParams = {} }) => {
  const chips = [];

  filterGroups.forEach(group => {
    if (group.type === 'price') {
      const raw = validQueryParams?.price;
      if (!raw) {
        return;
      }
      const [min, max] = String(raw).split(',');
      chips.push({
        id: `price-${raw}`,
        label: `$${min} – $${max}`,
        queryParamName: 'price',
        clearParams: { price: null },
      });
      return;
    }

    if (group.type === 'checkbox') {
      if (group.schemaType === 'category') {
        collectCheckedCategoryNodes(group.options || []).forEach(node => {
          const path = findCategoryOptionPath(group.options || [], node.id) || [];
          const root = path[0] || node;
          const department =
            departmentFromCategoryName(root.label) || departmentFromCategoryName(node.label);
          chips.push({
            id: `${group.id}-${node.id}`,
            label: node.label,
            queryParamName: group.queryParamName,
            optionId: node.id,
            group,
            department,
            rootLabel: root.label,
            clearParams: formatCategoryChangeParams(group, node.id, false),
          });
        });
        return;
      }

      (group.options || [])
        .filter(o => o.checked)
        .forEach(option => {
          chips.push({
            id: `${group.id}-${option.id}`,
            label: option.label,
            queryParamName: group.queryParamName,
            optionId: option.id,
            group,
            clearParams: formatOptionChangeParams(group, option.id, false),
          });
        });
    }
  });

  return chips;
};

/**
 * Build banner tabs from top-level listing categories (Shop / Book / Partner).
 *
 * @param {Array} listingCategories
 * @returns {Array<{ id: string, label: string }>}
 */
export const buildBannerTabs = (listingCategories = []) => {
  const preferred = ['shop', 'book', 'partner'];
  const topLevel = Array.isArray(listingCategories) ? listingCategories : [];

  const matchPreferred = key =>
    topLevel.find(cat => {
      const id = String(cat.id || '').toLowerCase();
      const name = String(cat.name || '').toLowerCase();
      return id === key || id.startsWith(`${key}-`) || name === key;
    });

  const byPreferred = preferred
    .map(matchPreferred)
    .filter(Boolean)
    .map(cat => ({
      id: cat.id,
      label: cat.name || cat.id,
    }));

  if (byPreferred.length > 0) {
    return byPreferred;
  }

  return topLevel.slice(0, 3).map(cat => ({
    id: cat.id,
    label: cat.name || cat.id,
  }));
};

/**
 * Category query param names used by nested category filter.
 */
export const CATEGORY_QUERY_PARAM_NAMES = [
  'pub_categoryLevel1',
  'pub_categoryLevel2',
  'pub_categoryLevel3',
];

/**
 * Build sort options for Racing toolbar from sortConfig.
 *
 * @param {Object} sortConfig
 * @param {Object} intl
 * @param {Object} options
 * @returns {Array<{ value: string, label: string, disabled?: boolean }>}
 */
export const buildSortOptions = (sortConfig, intl, { includeRelevance = false } = {}) => {
  const options = sortConfig?.options || [];
  return options
    .filter(option => includeRelevance || option.key !== sortConfig?.relevanceKey)
    .map(option => {
      const label = option.labelTranslationKey
        ? intl.formatMessage(
            { id: option.labelTranslationKey },
            option.translationValues ?? {}
          )
        : option.label || option.key;
      return {
        value: option.key,
        label,
      };
    });
};
